import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  alpha,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  PlaylistAdd as PlaylistAddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdicionarServicosModal = ({ 
  open, 
  onClose, 
  senha, 
  customTheme,
  onServicosAdicionados 
}) => {
  const [servicos, setServicos] = useState([]);
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [carregandoServicos, setCarregandoServicos] = useState(false);
  const [salvandoServicos, setSalvandoServicos] = useState(false);
  const [erro, setErro] = useState(null);

  // Buscar serviços disponíveis e carregar serviços já associados à senha
  useEffect(() => {
    if (open) {
      buscarServicos();
      carregarServicosExistentes();
    }
  }, [open]);

  // Limpar estado ao fechar modal
  useEffect(() => {
    if (!open) {
      setServicosSelecionados([]);
      setErro(null);
    }
  }, [open]);

  const buscarServicos = async () => {
    setCarregandoServicos(true);
    setErro(null);
    try {
      const response = await api.get('/servicos');
      if (response.data.success) {
        const servicosAtivos = response.data.data.filter(servico => servico.ativo);
        setServicos(servicosAtivos);
      } else {
        setErro('Erro ao carregar serviços disponíveis');
      }
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      setErro('Erro ao comunicar com o servidor');
    } finally {
      setCarregandoServicos(false);
    }
  };

  // Carregar serviços já associados à senha
  const carregarServicosExistentes = async () => {
    if (!senha?.servicos_adicionais || senha.servicos_adicionais.length === 0) {
      return;
    }

    try {
      // Buscar detalhes dos serviços já associados
      const response = await api.get('/servicos');
      if (response.data.success) {
        const todosServicos = response.data.data;
        
        // Filtrar apenas os serviços que já estão associados à senha
        const servicosJaAssociados = todosServicos.filter(servico => 
          senha.servicos_adicionais.includes(servico.nome)
        );
        
        setServicosSelecionados(servicosJaAssociados);
      }
    } catch (error) {
      console.error('Erro ao carregar serviços existentes:', error);
      // Não mostrar erro aqui, pois é secundário
    }
  };

  // Função para obter ícone do serviço baseado na abreviação
  const getServicoIcon = (abreviacao) => {
    const icones = {
      'SV': DescriptionIcon,
      'AP': AssignmentIcon,
      'AE': DescriptionIcon,
      'AT': GavelIcon,
      'ME': PersonIcon,
      'PA': AssignmentIcon,
      'PV': AssignmentIcon,
      'PP': AssignmentIcon,
      'PO': GavelIcon,
      'PE': DescriptionIcon,
      'RA': DescriptionIcon,
    };
    return icones[abreviacao] || BusinessIcon;
  };

  // Função para obter cor do serviço
  const getServicoCor = (abreviacao) => {
    const cores = {
      'SV': '#1976d2',
      'AP': '#388e3c',
      'AE': '#f57c00',
      'AT': '#7b1fa2',
      'ME': '#d32f2f',
      'PA': '#1976d2',
      'PV': '#388e3c',
      'PP': '#f57c00',
      'PO': '#7b1fa2',
      'PE': '#d32f2f',
      'RA': '#795548',
    };
    return cores[abreviacao] || '#424242';
  };

  // Adicionar serviço à seleção
  const handleAdicionarServico = (servico) => {
    if (!servicosSelecionados.find(s => s.id === servico.id)) {
      setServicosSelecionados([...servicosSelecionados, servico]);
    }
  };

  // Remover serviço da seleção
  const handleRemoverServico = (servicoId) => {
    setServicosSelecionados(servicosSelecionados.filter(s => s.id !== servicoId));
  };

  // Confirmar atualização dos serviços
  const handleConfirmar = async () => {
    setSalvandoServicos(true);
    try {
      // Chamar API para atualizar serviços da senha
      const response = await api.post(`/senhas/${senha.id}/adicionar-servicos`, {
        servicos: servicosSelecionados.map(s => s.nome)
      });

      if (response.data.success) {
        const totalServicos = servicosSelecionados.length;
        if (totalServicos === 0) {
          toast.success('Todos os serviços adicionais foram removidos!');
        } else {
          toast.success(`Serviços atualizados! Total: ${totalServicos} serviço(s)`);
        }
        onServicosAdicionados?.(servicosSelecionados);
        onClose();
      } else {
        toast.error('Erro ao atualizar serviços: ' + response.data.message);
      }
    } catch (error) {
      console.error('Erro ao atualizar serviços:', error);
      toast.error('Erro ao comunicar com o servidor');
    } finally {
      setSalvandoServicos(false);
    }
  };

  // Filtrar serviços disponíveis (excluir o serviço atual da senha)
  const servicosDisponiveis = servicos.filter(servico => 
    servico.nome !== senha?.tipo && // Excluir o serviço atual da senha
    !servicosSelecionados.find(s => s.id === servico.id) // Excluir já selecionados
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          minHeight: '500px'
        }
      }}
    >
      {/* Cabeçalho */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
          color: 'white',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PlaylistAddIcon sx={{ mr: 1.5, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Gerenciar Serviços do Atendimento
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Senha: {senha?.numero} • Serviço principal: {senha?.tipo}
            </Typography>
          </Box>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          sx={{
            backgroundColor: alpha('#fff', 0.1),
            '&:hover': {
              backgroundColor: alpha('#fff', 0.2)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Loading */}
        {carregandoServicos && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
              Carregando serviços...
            </Typography>
          </Box>
        )}

        {/* Erro */}
        {erro && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {erro}
            </Alert>
          </Box>
        )}

        {/* Conteúdo principal */}
        {!carregandoServicos && !erro && (
          <Box sx={{ p: 3 }}>
            {/* Informação sobre a funcionalidade */}
            <Alert 
              severity="info" 
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<InfoIcon />}
            >
              <Typography variant="body2">
                <strong>Como funciona:</strong> Os serviços selecionados abaixo serão os únicos serviços adicionais desta senha. 
                Você pode adicionar novos serviços ou remover existentes. Para remover todos os serviços adicionais, 
                desmarque todos e clique em "Remover Todos".
              </Typography>
            </Alert>

            {/* Serviços selecionados */}
            {servicosSelecionados.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  Serviços Selecionados ({servicosSelecionados.length})
                </Typography>
                <List sx={{ bgcolor: alpha(customTheme.palette.success.main, 0.05), borderRadius: 2, p: 1 }}>
                  {servicosSelecionados.map((servico, index) => {
                    const IconComponent = getServicoIcon(servico.abreviacao);
                    return (
                      <Fade in={true} key={servico.id} timeout={300 + index * 100}>
                        <ListItem
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: alpha(customTheme.palette.success.main, 0.2)
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                            <IconComponent sx={{ color: getServicoCor(servico.abreviacao), fontSize: 20 }} />
                          </Box>
                          <ListItemText
                            primary={servico.nome}
                            secondary={`${servico.abreviacao} • ${servico.descricao || 'Sem descrição'}`}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Remover serviço">
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoverServico(servico.id)}
                                size="small"
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: alpha(customTheme.palette.error.main, 0.1)
                                  }
                                }}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Fade>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Divisor */}
            {servicosSelecionados.length > 0 && servicosDisponiveis.length > 0 && (
              <Divider sx={{ my: 3 }}>
                <Chip label="Serviços Disponíveis" size="small" />
              </Divider>
            )}

            {/* Serviços disponíveis */}
            {servicosDisponiveis.length > 0 ? (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Serviços Disponíveis ({servicosDisponiveis.length})
                </Typography>
                <Grid container spacing={2}>
                  {servicosDisponiveis.map((servico, index) => {
                    const IconComponent = getServicoIcon(servico.abreviacao);
                    const cor = getServicoCor(servico.abreviacao);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={servico.id}>
                        <Zoom in={true} timeout={300 + index * 50}>
                          <Card
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              border: '2px solid transparent',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 24px ${alpha(cor, 0.25)}`,
                                borderColor: alpha(cor, 0.3)
                              }
                            }}
                            onClick={() => handleAdicionarServico(servico)}
                          >
                            <CardContent sx={{ pb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(cor, 0.1),
                                    mr: 1.5
                                  }}
                                >
                                  <IconComponent sx={{ color: cor, fontSize: 24 }} />
                                </Box>
                                <Chip
                                  label={servico.abreviacao}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(cor, 0.1),
                                    color: cor,
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {servico.nome}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                                {servico.descricao || 'Sem descrição disponível'}
                              </Typography>
                            </CardContent>
                            <CardActions sx={{ pt: 0, justifyContent: 'center' }}>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                size="small"
                                sx={{
                                  borderColor: cor,
                                  color: cor,
                                  '&:hover': {
                                    borderColor: cor,
                                    bgcolor: alpha(cor, 0.1)
                                  }
                                }}
                              >
                                Adicionar
                              </Button>
                            </CardActions>
                          </Card>
                        </Zoom>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nenhum serviço disponível
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Todos os serviços já foram selecionados ou não há serviços ativos
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Rodapé */}
      <DialogActions
        sx={{
          p: 3,
          bgcolor: alpha(customTheme.palette.background.default, 0.5),
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          disabled={salvandoServicos}
          startIcon={salvandoServicos ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            background: `linear-gradient(45deg, ${customTheme.palette.success.main}, ${customTheme.palette.success.dark})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${customTheme.palette.success.dark}, ${customTheme.palette.success.main})`
            }
          }}
        >
          {salvandoServicos ? 'Salvando...' : servicosSelecionados.length === 0 ? 'Remover Todos' : `Salvar (${servicosSelecionados.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdicionarServicosModal;
